import { Spinner } from 'components/Shared';
import React, { type PropsWithChildren, useEffect, } from 'react';
import { type IConfig } from './interfaces';
import { useSessionFlag } from 'hooks/useSessionFlag';
import { type ValidAppRegion } from 'utils/regionUtils';
import { mockApiConfigs } from 'providers/RegionConfig/mockRegions';

const AppConfigContext = React.createContext<IConfig>({
    AppVersion: '',
    IDLE_TIMEOUT_VALUE_IN_MILISECONDS: 0,
    REACT_APP_GRAPHQL_API_HOST: '',
    REACT_APP_GRAPHQL_WS_HOST: '',
    REACT_APP_FILEUPLOAD_HOST: '',
    REACT_APP_CREDENTIALS_HOST: '',
    REACT_APP_CI_REFERRAL_HOST: '',
    REACT_APP_DOC_REQUEST_HOST: '',
    REACT_APP_INCIDENT_REPORT_HOST: '',
    REFRESH_TIMEOUT_VALUE_IN_MILISECONDS: 0,
    FETCH_LATEST_CONFIG_INTERVAL: 0,
    CACHE_EXPIRY_IN_MILLISECONDS: 300000,
    QUERYCLIENT_STALETIME_IN_MILLISECONDS: 300000,
    QUERYCLIENT_GC_IN_MILLISECONDS: 600000,
    INVOICE_DRAFT_EXPIRY_IN_HOURS: 12,
    RegionConfigs: [],
    Dynatrace: undefined,
});

AppConfigContext.displayName = 'AppConfigContext';

const AppConfigProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [config, setConfig] = React.useState<IConfig | null>(null);
    let { current: refreshTimeout } = React.useRef<number | null>(null);
    const forcedRegion = useSessionFlag<ValidAppRegion>('forced-region');

    const fetchConfig = (): Promise<IConfig> => (
        fetch('/config.json', { cache: 'no-store' }).then(response => response.json())
    )

    const updateConfig = async () => {
        const newConfig = await fetchConfig();
        if (config && newConfig.AppVersion !== config.AppVersion) {
            setConfig(newConfig);
        }
        refreshTimeout && clearTimeout(refreshTimeout);
        refreshTimeout = window.setTimeout(updateConfig, newConfig?.FETCH_LATEST_CONFIG_INTERVAL || 3_600_000);
    }
    
    useEffect(() => {
        fetchConfig()
            .then((res) => {
                setConfig({
                    ...res,
                    ...(forcedRegion ? mockApiConfigs[forcedRegion] : {})
                });
                refreshTimeout && clearTimeout(refreshTimeout);
                refreshTimeout = window.setTimeout(updateConfig, res?.FETCH_LATEST_CONFIG_INTERVAL || 3_600_000);
            })
    }, []);

    return !config
        ? <Spinner fillParent/>
        : <AppConfigContext.Provider value={config} children={children}/>;
}

const useAppConfig = () => React.useContext(AppConfigContext);

export { AppConfigProvider, useAppConfig };
